import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, TrendingUp, AlertTriangle, ChevronDown, GraduationCap, BookOpen, Users, Globe, Clock, DollarSign, Shield, Shuffle, Coffee, ArrowRight, Star, Zap, Target, Route } from 'lucide-react';
import { PathResult, PathMatchScore, VersionConfig } from '@/types/assessment';
import { getEduIcon } from '@/lib/eduIcons';
import { scoreRiasec, topRiasec, riasecSummary, riasecHollandCode } from '@/lib/riasec';
import { demandBand, resilienceBand } from '@/lib/outlook';

interface SmartRecommendationsProps {
  paths: PathResult[];
  matches: PathMatchScore[];
  config: VersionConfig;
  shortlisted: number[];
  onToggleShortlist: (index: number) => void;
  onViewComparison: () => void;
  selections?: Record<string, string[]>;
}

const pathGradients = [
  'from-sky-100 to-blue-50 border-sky-200',
  'from-violet-100 to-purple-50 border-violet-200',
  'from-amber-100 to-orange-50 border-amber-200',
];

const pathAccentColors = [
  'text-sky-600 bg-sky-100',
  'text-violet-600 bg-violet-100',
  'text-amber-600 bg-amber-100',
];

const pathIcons = [Shield, Shuffle, Zap];

const SmartRecommendations = ({
  paths,
  matches,
  config,
  shortlisted,
  onToggleShortlist,
  onViewComparison,
  selections,
}: SmartRecommendationsProps) => {
  const sorted = [...matches].sort((a, b) => b.matchPercent - a.matchPercent);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<string>('roadmap');

  // Honest framing: we can truthfully state the interest profile that
  // drove the ranking (we computed it deterministically from the user's
  // own answers). We do NOT fabricate a per-path RIASEC match because
  // PathResult carries no RIASEC field — claiming one would be the
  // generic AI fluff we explicitly avoid.
  const riasec = selections ? scoreRiasec(selections) : null;
  const topCodes = riasec ? topRiasec(riasec, 3) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-primary" />
        <h3 className="font-display font-semibold text-foreground text-lg">Top Matches For You</h3>
      </div>

      {topCodes.length > 0 && (
        <div className="rounded-xl border border-primary/25 bg-primary/[0.06] px-4 py-3">
          <p className="text-xs text-muted-foreground leading-relaxed">
            These paths were ranked against your interest profile:{' '}
            <span className="font-semibold text-foreground">
              {riasecSummary(topCodes)}
            </span>{' '}
            <span className="font-mono text-[10px] text-primary">({riasecHollandCode(topCodes)})</span>.
            Match scores combine that interest fit with the skills you reported and live O*NET labor data.
          </p>
        </div>
      )}

      <div className="space-y-5">
        {sorted.map((match, rank) => {
          const path = paths[match.pathIndex];
          // Use AI-generated career title (actual job name) from path.name
          const pathName = path?.name || `Path ${match.pathIndex + 1}`;
          const isShortlisted = shortlisted.includes(match.pathIndex);
          const isExpanded = expandedCard === match.pathIndex;
          const PathIcon = pathIcons[match.pathIndex] || Shield;
          const gradient = pathGradients[match.pathIndex] || pathGradients[0];
          const accentColor = pathAccentColors[match.pathIndex] || pathAccentColors[0];

          return (
            <motion.div
              key={match.pathIndex}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: rank * 0.12, type: 'spring', stiffness: 120 }}
              className={`rounded-2xl overflow-hidden border transition-all ${
                isShortlisted ? 'ring-2 ring-primary ring-offset-2' : ''
              } ${gradient.split(' ').pop()}`}
            >
              {/* Card Header — Illustrated */}
              <div className={`p-5 bg-gradient-to-br ${gradient}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${accentColor}`}>
                      <PathIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {rank === 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider">
                            <Star className="w-3 h-3" /> Best Match
                          </span>
                        )}
                        <h4 className="font-display font-bold text-foreground text-lg">{pathName}</h4>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{path.description}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="relative w-16 h-16">
                      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                        <circle cx="32" cy="32" r="28" fill="none" stroke="currentColor" strokeWidth="4" className="text-border/50" />
                        <circle
                          cx="32" cy="32" r="28" fill="none"
                          stroke="currentColor" strokeWidth="4"
                          strokeDasharray={`${(match.matchPercent / 100) * 175.9} 175.9`}
                          strokeLinecap="round"
                          className="text-primary"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-sm font-display font-bold text-foreground">
                        {match.matchPercent}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Match reasons as tags */}
                {match.reasons.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-foreground/50 mb-1.5">
                      Why this ranked #{rank + 1} for you
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {match.reasons.map((reason, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/60 backdrop-blur-sm text-xs text-foreground/80 border border-white/40">
                          <TrendingUp className="w-3 h-3 text-primary shrink-0" />
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Day In The Life — Story-Driven */}
              {path.dayInTheLife && (
                <div className="px-5 py-4 bg-white/50 border-t border-border/20">
                  <div className="flex items-start gap-2">
                    <Coffee className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-foreground mb-1">A day in this life</p>
                      <p className="text-xs text-muted-foreground leading-relaxed italic">{path.dayInTheLife}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Skill gaps */}
              {match.skillGaps.length > 0 && (
                <div className="px-5 py-3 bg-white/30 border-t border-border/20">
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlertTriangle className="w-3 h-3 text-accent" />
                    <span className="text-xs font-medium text-accent">Skills to develop</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {match.skillGaps.map(gap => (
                      <div key={gap.skill} className="space-y-1">
                        <div className="flex justify-between text-[10px]">
                          <span className="text-muted-foreground truncate">{gap.skill}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <motion.div
                            className="h-full rounded-full bg-primary"
                            initial={{ width: 0 }}
                            animate={{ width: `${(gap.current / gap.needed) * 100}%` }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                          />
                        </div>
                        <p className="text-[9px] text-muted-foreground">{gap.current}% → {gap.needed}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nonlinear Career Insights */}
              {path.nonlinearInsights && (
                <div className="px-5 py-3 bg-white/30 border-t border-border/20">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Route className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-semibold text-foreground">Nonlinear career paths</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Anthropic Research</span>
                  </div>
                  <div className="space-y-2">
                    {path.nonlinearInsights.unexpectedPaths.map((p, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <ArrowRight className="w-3 h-3 text-primary shrink-0" />
                        <span>{p}</span>
                      </div>
                    ))}
                  </div>
                  {/* AI Exposure Badge */}
                  <div className={`mt-2 p-2 rounded-lg border ${
                    path.nonlinearInsights.aiExposure.level === 'low' ? 'bg-emerald-50 border-emerald-200' :
                    path.nonlinearInsights.aiExposure.level === 'moderate' ? 'bg-amber-50 border-amber-200' :
                    'bg-rose-50 border-rose-200'
                  }`}>
                    <div className="flex items-center gap-1.5">
                      <Shield className={`w-3 h-3 ${
                        path.nonlinearInsights.aiExposure.level === 'low' ? 'text-emerald-600' :
                        path.nonlinearInsights.aiExposure.level === 'moderate' ? 'text-amber-600' :
                        'text-rose-600'
                      }`} />
                      <span className="text-[10px] font-semibold text-foreground">
                        AI Exposure: {path.nonlinearInsights.aiExposure.level.charAt(0).toUpperCase() + path.nonlinearInsights.aiExposure.level.slice(1)}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{path.nonlinearInsights.aiExposure.adaptationStrategy}</p>
                  </div>
                </div>
              )}

              {/* Expand toggle */}
              <button
                onClick={() => setExpandedCard(isExpanded ? null : match.pathIndex)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors w-full justify-center py-3 border-t border-border/20 bg-white/20"
              >
                <span>{isExpanded ? 'Hide Full Roadmap' : 'View Full Roadmap'}</span>
                <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </motion.div>
              </button>

              {/* Expanded roadmap content */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 space-y-4 bg-white/20">
                      {/* Section tabs */}
                      <div className="flex gap-1.5 overflow-x-auto pb-1">
                        {[
                          { id: 'roadmap', label: 'Timeline', icon: Clock },
                          { id: 'education', label: 'Education', icon: GraduationCap },
                          { id: 'skills', label: 'Skills', icon: BookOpen },
                          { id: 'outlook', label: 'Outlook', icon: Globe },
                        ].map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveSection(tab.id)}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                              activeSection === tab.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-white/50 text-muted-foreground hover:bg-white/80'
                            }`}
                          >
                            <tab.icon className="w-3 h-3" />
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Interactive Timeline */}
                      {activeSection === 'roadmap' && (
                        <div className="space-y-0">
                          {!path.actions && (
                            <div className="text-center py-6 text-xs text-muted-foreground">
                              Loading detailed roadmap...
                            </div>
                          )}
                          {path.actions && config.timelines.map((timeline, tIdx) => {
                            const actions = path.actions?.[timeline];
                            const skills = path.skills?.[timeline];
                            if (!actions) return null;
                            return (
                              <div key={timeline} className="relative pl-6">
                                {/* Timeline line */}
                                {tIdx < config.timelines.length - 1 && (
                                  <div className="absolute left-[9px] top-6 bottom-0 w-0.5 bg-primary/20" />
                                )}
                                {/* Timeline dot */}
                                <div className="absolute left-0 top-1 w-[18px] h-[18px] rounded-full bg-primary/20 flex items-center justify-center">
                                  <div className="w-2 h-2 rounded-full bg-primary" />
                                </div>
                                <div className="pb-5">
                                  <h5 className="font-display font-semibold text-primary text-sm mb-2">{timeline}</h5>
                                  <div className="space-y-1.5">
                                    {actions.map((action, i) => (
                                      <div key={i} className="flex items-start gap-1.5 text-xs">
                                        <span className="text-primary mt-0.5 shrink-0">▸</span>
                                        <span className="text-foreground/90">{action}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {skills && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {skills.map((skill) => (
                                        <span key={skill} className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px]">{skill}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Educational Paths */}
                      {activeSection === 'education' && path.educationalPaths && (
                        <div className="space-y-2">
                          {path.educationalPaths.map((ep) => {
                            const EduIcon = getEduIcon(ep.type);
                            return (
                              <div key={ep.name} className="glass-card p-3 space-y-1">
                                <div className="flex items-start gap-2">
                                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <EduIcon className="w-3.5 h-3.5 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-foreground text-xs">{ep.name}</h5>
                                    <p className="text-[10px] text-muted-foreground mt-0.5">{ep.description}</p>
                                    <div className="flex gap-3 mt-1">
                                      <span className="text-[10px] text-primary flex items-center gap-0.5">
                                        <Clock className="w-2.5 h-2.5" /> {ep.duration}
                                      </span>
                                      <span className="text-[10px] text-accent flex items-center gap-0.5">
                                        <DollarSign className="w-2.5 h-2.5" /> {ep.cost}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Skills to Stack */}
                      {activeSection === 'skills' && (
                        <div className="space-y-3">
                          {!path.skillsToStack && (
                            <div className="text-center py-6 text-xs text-muted-foreground">
                              Loading skill recommendations...
                            </div>
                          )}
                          {path.skillsToStack?.courses && path.skillsToStack.courses.length > 0 && (
                            <div className="glass-card p-3 space-y-2">
                              <h5 className="text-xs font-semibold text-primary">Courses & Learning</h5>
                              {path.skillsToStack.courses.slice(0, 3).map((c, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs">
                                  <span className="text-primary shrink-0">▸</span>
                                  <span className="text-foreground/90">{c}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {path.skillsToStack?.portfolio && path.skillsToStack.portfolio.length > 0 && (
                            <div className="glass-card p-3 space-y-2">
                              <h5 className="text-xs font-semibold text-accent">Portfolio</h5>
                              {path.skillsToStack.portfolio.slice(0, 3).map((p, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs">
                                  <span className="text-accent shrink-0">▸</span>
                                  <span className="text-foreground/90">{p}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {path.skillsToStack?.connections && path.skillsToStack.connections.length > 0 && (
                            <div className="glass-card p-3 space-y-2">
                              <h5 className="text-xs font-semibold text-foreground flex items-center gap-1">
                                <Users className="w-3 h-3" /> Connections
                              </h5>
                              {path.skillsToStack.connections.slice(0, 3).map((c, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs">
                                  <span className="text-primary shrink-0">▸</span>
                                  <span className="text-foreground/90">{c}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Global Outlook */}
                      {activeSection === 'outlook' && (
                        <div className="space-y-3">
                          <div className="glass-card p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold text-foreground">Top Region: {path.globalOutlook.topRegion}</span>
                              <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">
                                {path.globalOutlook.growthRate}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 rounded-lg bg-primary/10">
                                <p className="text-sm font-display font-bold text-primary">{demandBand(path.globalOutlook.demandScore)}</p>
                                <p className="text-[9px] text-muted-foreground">Demand (est.)</p>
                              </div>
                              <div className="text-center p-2 rounded-lg bg-secondary">
                                <p className="text-xs font-bold text-foreground">{path.globalOutlook.avgSalary}</p>
                                <p className="text-[9px] text-muted-foreground">Avg Salary</p>
                              </div>
                              <div className="text-center p-2 rounded-lg bg-accent/10">
                                <p className="text-sm font-display font-bold text-accent">{resilienceBand(path.globalOutlook.aiResilienceScore)}</p>
                                <p className="text-[9px] text-muted-foreground">AI Resilience (est.)</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 pt-2 border-t border-border/50">
                              {path.globalOutlook.trendingRoles.map(role => (
                                <span key={role} className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px]">{role}</span>
                              ))}
                            </div>
                          </div>

                          {/* Monetize Now / Projects to Start */}
                          {path.monetizeNow && path.monetizeNow.length > 0 && (
                            <div className="glass-card p-3 space-y-2 border-accent/30">
                              <h5 className="font-semibold text-accent text-xs flex items-center gap-1.5">
                                {config.edition === 'highschool' ? (
                                  <><Zap className="w-3.5 h-3.5" /> Projects to Start</>
                                ) : (
                                  <><DollarSign className="w-3.5 h-3.5" /> Monetize Now</>
                                )}
                              </h5>
                              {path.monetizeNow.map((item, i) => (
                                <div key={i} className="flex items-start gap-1.5 text-xs">
                                  <span className="text-accent shrink-0">▸</span>
                                  <span className="text-foreground/90">{item}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Future Proofing */}
                          {path.futureProofing && path.futureProofing.length > 0 && (
                            <div className="glass-card p-3 space-y-2 border-primary/30">
                              <h5 className="font-semibold text-primary text-xs flex items-center gap-1.5">
                                <TrendingUp className="w-3.5 h-3.5" /> What's Shifting
                              </h5>
                              {path.futureProofing.map((tip, i) => (
                                <p key={i} className="text-xs text-muted-foreground italic">{tip}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Shortlist toggle */}
              <button
                onClick={() => onToggleShortlist(match.pathIndex)}
                className={`w-full py-2.5 text-sm font-medium transition-all border-t ${
                  isShortlisted
                    ? 'bg-primary/10 text-primary border-primary/20'
                    : 'bg-white/30 text-muted-foreground hover:text-primary border-border/20'
                }`}
              >
                {isShortlisted ? '✓ Shortlisted' : '+ Add to Shortlist'}
              </button>
            </motion.div>
          );
        })}
      </div>

      {shortlisted.length >= 2 && (
        <motion.button
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onViewComparison}
          className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/20"
        >
          Compare {shortlisted.length} Paths Side-by-Side →
        </motion.button>
      )}
    </div>
  );
};

export default SmartRecommendations;
